import React from 'react';
import { Preset, FunctionColor, Text, SupportingColor } from '@lux/text';
import { CategoryTab } from '@shared/types/mylounge';
import { ChevronUpXS, ChevronDownXS } from '@lux/zds-icons';

import { ErrorBoundary } from '@browsing/components';
import { CategoryTabIds } from '@shared/constants';

import * as S from './tab.styles';
import { useGRE } from '../../hooks';
import { useCrossCampaignEntryLargeScreen } from '../../cross-campaign/hooks/use-cross-campaign-entry-large-screen';

type CrossCampaignEntryProps = ReturnType<typeof useCrossCampaignEntryLargeScreen>;

type TabProps = CrossCampaignEntryProps & {
    tab: CategoryTab;
    index: number;
    name: string;
    active: boolean;
    onAction: (index: number) => void;
};

export const Tab = ({
    tab,
    index,
    name,
    active,
    onAction,
    showMenu,
    hideMenu,
    isMenuShown,
    isExpandableTab,
    CrossCampaignEntryLargeScreen,
}: TabProps) => {
    const { checkIfGRETab } = useGRE();
    const isGRETab = checkIfGRETab(tab);

    const handleAction = () => {
        onAction(index);
    };

    const isMenuShownForTab = isMenuShown(tab.id as CategoryTabIds);

    return (
        <S.ListItem>
            <S.Button
                role="link"
                tabIndex={0}
                onClick={handleAction}
                data-testid={tab.id}
                active={active}
                aria-current={active}
                isGRETab={isGRETab}
                onMouseEnter={() => showMenu(tab.id as CategoryTabIds)}
                onMouseLeave={() => hideMenu(250)}
                onFocus={() => {
                    hideMenu(0);
                }}
            >
                <Text
                    preset={Preset.S}
                    bold
                    color={isGRETab ? SupportingColor.alert : FunctionColor.function100}
                >
                    {name}
                </Text>
            </S.Button>
            {!isExpandableTab(tab.id as CategoryTabIds) ? null : (
                <S.AccessibilityButton
                    aria-label={name}
                    onClick={() => {
                        if (isMenuShownForTab) {
                            hideMenu(0);
                        } else {
                            showMenu(tab.id as CategoryTabIds);
                        }
                    }}
                    onFocus={() => hideMenu(0)}
                    onKeyDown={event => {
                        if ((event.shiftKey && event.key === 'Tab') || event.key === 'Escape') {
                            hideMenu(0);
                        }
                    }}
                    aria-expanded={isMenuShownForTab}
                >
                    {isMenuShownForTab ? <ChevronUpXS /> : <ChevronDownXS />}
                </S.AccessibilityButton>
            )}
            <ErrorBoundary componentName="CrossCampaignEntryLargeScreen">
                {isMenuShown(tab.id as CategoryTabIds) && <CrossCampaignEntryLargeScreen />}
            </ErrorBoundary>
        </S.ListItem>
    );
};
